import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Logos } from "@optymyse/suite-id";
import {
  peopleRights,
  type AppAccess,
  type OrganisationIcon,
  type OrganisationRole,
  type PeopleViewer,
} from "@optymyse/suite-ui";
import { api, ApiError, jsonOrThrow } from "./api";
import { ME_KEY, type MeAnswer, type MeWorkspace } from "./auth";
import type { WorkspaceInView } from "../../shared/types/workspaces";
import type { WorkspaceRole } from "../../shared/schemas/workspaces";

// Every call here is about the workspace whose host the page is on (src/shared/hosts.ts): the API reads
// the workspace from the host, and `slug`, the same host's, keys what is kept of the answers. The
// workspaces a person may open are GET /api/v1/me's (./auth).
const WORKSPACE_KEY = (slug: string) => ["workspace", slug] as const;

/**
 * The workspace in view, with its organisation's logos, and the person in it as Impa decided just now: their role in
 * Link, and their role in the organisation and their access to Link, which the People page is shown by
 * (src/domains/workspaces/routes.ts).
 */
export type WorkspaceView = {
  workspace: WorkspaceInView;
  role: WorkspaceRole;
  organisationRole: OrganisationRole;
  productRole: AppAccess;
};

/**
 * A workspace's organisation's icon as the kit shows it (its OrganisationMark), from the logos Impa's decision carried:
 * its light version, and its dark one when it has one, each at the address Impa serves it from. Null where Impa holds
 * none, and the kit draws its own: the building in the top bar, the name's initial on the door's choice. As Sustain's
 * console reads them (its src/lib/images.ts).
 */
export function organisationIcon(logos: Logos): OrganisationIcon | null {
  return logos.icon ? { light: logos.icon.light.url, dark: logos.icon.dark?.url ?? null } : null;
}

/**
 * Whether the central host has a choice of workspaces to show the person, and so whether the top bar's menu offers
 * "All your workspaces": they have more than one in Link, or Optymyse couldn't be asked about some of them just now.
 * With one, and none unchecked, the central host would only send them straight back to it (onlyWorkspace), so the bar
 * names the workspace without a menu, as Assure's does (the owner, 28 October 2026). Nothing is offered until
 * `GET /api/v1/me` has answered.
 */
export function hasWorkspacesToChoose(answer: MeAnswer | undefined): boolean {
  return answer?.kind === "signed_in" && (answer.me.workspaces.length > 1 || answer.me.unchecked > 0);
}

/**
 * The workspace the central host opens straight away, behind Link's launch: the person's one workspace, with none
 * Optymyse couldn't check, as Assure's root has it. With some unchecked, the choice is shown instead, saying so, so
 * the top bar's "All your workspaces" never leads straight back where it came from (hasWorkspacesToChoose).
 */
export function onlyWorkspace(answer: MeAnswer | undefined): MeWorkspace | null {
  return answer?.kind === "signed_in" && !hasWorkspacesToChoose(answer) ? (answer.me.workspaces[0] ?? null) : null;
}

/** The person looking, as the People page and its rules take them, once the workspace and who they are are known. */
export function peopleViewer(view: WorkspaceView | undefined, userId: string | undefined): PeopleViewer | null {
  return view && userId ? { id: userId, organisationRole: view.organisationRole, access: view.productRole } : null;
}

/** Whether People is for them: the organisation's owners and admins, and Link's Admins (SuiteUi's peopleRights). */
export function seesPeople(viewer: PeopleViewer | null): boolean {
  return viewer !== null && peopleRights(viewer).sees;
}

export function useWorkspace(slug: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: WORKSPACE_KEY(slug),
    queryFn: async () => {
      const res = await api.api.v1.workspace.$get();
      try {
        return await jsonOrThrow<WorkspaceView>(res);
      } catch (err) {
        // A refusal also removed the membership, so the workspace list no longer holds it.
        if (err instanceof ApiError && err.status === 403) void qc.invalidateQueries({ queryKey: ME_KEY });
        throw err;
      }
    },
    staleTime: 30_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 401 || err.status === 403)) return false;
      return failureCount < 1;
    },
    // A refusal is shown in place of the workspace, header and all, and the header reads this query
    // too: fetched again whenever a reader mounted, it would take the refusal away and bring it back.
    retryOnMount: false,
  });
}
